"use client";

import React, { useMemo } from "react";
import { useLocale, useTranslations } from "next-intl";
import { Box, Typography } from "@mui/material";
import type { CanvasVariant } from "src/components/canvas/variant-resolver";
import { catalogueOptions, variantAxesOf } from "./artwork-purchase";
import { formatSize, useMaterialLabel } from "./useCardOffers";

type ListFormatCtor = new (
  locale: string,
  options: { type: "disjunction" | "conjunction"; style?: "long" | "short" },
) => { format: (items: string[]) => string };

/** "mat ali sijaj" in the page's language; a plain slash where Intl cannot. */
function orList(items: string[], locale: string): string {
  const ListFormat = (Intl as unknown as { ListFormat?: ListFormatCtor }).ListFormat;
  if (!ListFormat) return items.join(" / ");
  try {
    return new ListFormat(locale, { type: "disjunction" }).format(items);
  } catch {
    return items.join(" / ");
  }
}

/** Mid-sentence, a label's capital goes; German nouns later in it keep theirs. */
const lowerFirst = (text: string, locale: string) =>
  text ? text.charAt(0).toLocaleLowerCase(locale) + text.slice(1) : text;

/**
 * What every work can be printed as, said once at the top of the gallery:
 * each material and the finishes it comes in, and the range of sizes.
 *
 * Read off the formats on sale (catalogueOptions), named with the canvas
 * editor's own labels, so it says exactly what the size pickers will offer —
 * no frame for paper unless the shop sells one, no gloss canvas. A value with
 * no label in the editor's copy is left out rather than shown as a code.
 */
export default function CatalogueOptionsLine({
  variants,
}: {
  variants: CanvasVariant[] | null | undefined;
}) {
  const t = useTranslations("art");
  const tEditor = useTranslations("editor");
  const locale = useLocale();
  const axes = useMemo(() => variantAxesOf(variants), [variants]);
  const materialLabel = useMaterialLabel(axes);
  const options = useMemo(() => catalogueOptions(variants, axes), [variants, axes]);

  if (!options.materials.length) return null;

  const labelsOf = (prefix: string, values: string[]) =>
    values.length > 1
      ? values
          .filter((v) => tEditor.has(`${prefix}_${v}`))
          .map((v) => lowerFirst(tEditor(`${prefix}_${v}`), locale))
      : [];

  const items = options.materials.map(({ material, surfaces, mounts }) => {
    const details = [labelsOf("canvasSurface", surfaces), labelsOf("canvasMount", mounts)]
      .filter((labels) => labels.length > 1)
      .map((labels) => orList(labels, locale));
    return { key: material, label: materialLabel(material), detail: details.join(", ") };
  });
  if (options.sizes) {
    const { from, to } = options.sizes;
    items.push({
      key: "sizes",
      label: t("optionsSizes"),
      detail:
        formatSize(from) === formatSize(to)
          ? t("sizeSingle", { size: formatSize(from) })
          : t("sizeRange", { from: formatSize(from), to: formatSize(to) }),
    });
  }

  return (
    <Box
      component="ul"
      aria-label={t("optionsLabel")}
      sx={{
        listStyle: "none",
        p: 0,
        m: 0,
        mt: 1.25,
        display: "flex",
        flexWrap: "wrap",
        columnGap: 3,
        rowGap: 0.5,
      }}
    >
      {items.map((item) => (
        <Typography
          key={item.key}
          component="li"
          variant="body2"
          sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}
        >
          <Box
            component="span"
            aria-hidden
            sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "primary.main", flexShrink: 0, alignSelf: "center" }}
          />
          <Box component="span" sx={{ fontWeight: 700 }}>
            {item.label}
          </Box>
          {item.detail ? (
            <Box component="span" sx={{ color: "text.secondary" }}>
              {item.detail}
            </Box>
          ) : null}
        </Typography>
      ))}
    </Box>
  );
}
